import clsx from "clsx";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";

import type { Id } from "../../../api/types";
import { ProgressRing, StateBadge } from "../../../ui";
import { isDimmed, type Highlight } from "../model/filters";
import { levelWords, progressWords, visibleRows, type NavModel, type NavNode } from "../model/nav";

interface NavigatorListProps {
  model: NavModel;
  expanded: ReadonlySet<Id>;
  onToggle(id: Id): void;
  /** Names the list for screen readers. */
  label: string;
  selectedId?: Id | null;
  /** The name is a button that chooses the node. */
  onSelect?(id: Id): void;
  /** The name is a link instead (the home page). */
  hrefFor?(node: NavNode): string;
  highlight?: Highlight | null;
  /** Nodes that have just arrived: they pop in. */
  arrivals?: ReadonlySet<Id>;
}

/**
 * The family tree as a list: the whole tree is always there (DEC-1016-50). Each row has a ring (validated and
 * waiting for the SME), the level in words and the state in words. Rows open and fold with a button, so the
 * list works from the keyboard and with a screen reader; the map beside it is the same tree drawn.
 */
export function NavigatorList({ model, expanded, onToggle, label, selectedId = null, onSelect, hrefFor, highlight = null, arrivals }: NavigatorListProps) {
  const rows = visibleRows(model, expanded);
  return (
    <ul className="nv-tree" aria-label={label}>
      {rows.map(({ node, depth, hasChildren, expanded: open }) => {
        const progress = model.progress.get(node.id) ?? null;
        const chosen = node.id === selectedId;
        return (
          <li
            key={node.id}
            className={clsx("nv-row", isDimmed(highlight, node.id) && "nv-row--dim", arrivals?.has(node.id) && "nv-row--new", chosen && "nv-row--chosen")}
            style={{ "--depth": depth } as CSSProperties}
            data-level={node.kind}
            data-state={node.state ?? undefined}
          >
            {hasChildren ? (
              <button type="button" className="nv-row__toggle" aria-expanded={open} aria-label={`${open ? "Fold" : "Open"} ${node.label}`} onClick={() => onToggle(node.id)}>
                <span aria-hidden="true">{open ? "▾" : "▸"}</span>
              </button>
            ) : (
              <span className="nv-row__toggle nv-row__toggle--none" aria-hidden="true" />
            )}
            <ProgressRing validated={progress?.validated ?? 0} waiting={progress?.waiting ?? 0} total={progress?.total ?? 0} size={26} />
            {hrefFor ? (
              <Link to={hrefFor(node)} className="nv-row__name" aria-current={chosen ? "true" : undefined} title={progressWords(progress)}>
                {node.label}
              </Link>
            ) : (
              <button type="button" className="nv-row__name" aria-current={chosen ? "true" : undefined} title={progressWords(progress)} onClick={() => onSelect?.(node.id)}>
                {node.label}
              </button>
            )}
            <span className="nv-row__level muted">{levelWords(model, node)}</span>
            {(node.abstract || !node.drawn) && node.kind === "item" && <span className="nv-row__note muted">not drawn</span>}
            {node.state && <StateBadge state={node.state} />}
            {hasChildren && !open && <span className="nv-row__more muted">{model.below.get(node.id)} below</span>}
          </li>
        );
      })}
    </ul>
  );
}
